import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Loader2, Calendar, ArrowLeft } from 'lucide-react';
import { getChampions } from '../lib/api';

const ChampionCard = ({ champion }) => {
  const hasPhoto = champion.photo_data && champion.photo_content_type;
  return (
    <article
      className="group relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60 hover:border-gold/40 transition-all hover:-translate-y-1"
      data-testid={`champion-card-${champion.id}`}
    >
      <div className="aspect-[4/3] w-full bg-slate-950 overflow-hidden">
        {hasPhoto ? (
          <img
            src={`data:${champion.photo_content_type};base64,${champion.photo_data}`}
            alt={`Vincitori ${champion.year} - ${champion.team_name}`}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-700">
            <Trophy className="w-20 h-20" />
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-numbers text-xl font-bold text-gold">{champion.year}</span>
          {champion.edition ? (
            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-800 text-slate-400 border border-slate-700">
              {champion.edition}ª edizione
            </span>
          ) : null}
        </div>
        {champion.team_name ? (
          <h3 className="font-heading font-bold text-lg leading-tight">{champion.team_name}</h3>
        ) : null}
        {champion.notes ? <p className="mt-1 text-xs text-slate-400">{champion.notes}</p> : null}
      </div>
    </article>
  );
};

export const AlboDoroPage = () => {
  const [champions, setChampions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Albo d'Oro - Torneo Notturno Viadana Bresciana";
    getChampions()
      .then((res) => setChampions(res.data || []))
      .catch(() => setChampions([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="py-8 sm:py-12 px-4 min-h-screen" data-testid="albo-doro-page">
      <div className="max-w-6xl mx-auto">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-primary mb-4 transition-colors"
          data-testid="back-home-link"
        >
          <ArrowLeft className="w-3 h-3" /> Torna alla Home
        </Link>

        <div className="text-center mb-10 sm:mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/10 border border-gold/30 rounded-full mb-4">
            <Trophy className="w-4 h-4 text-gold" />
            <span className="text-sm font-semibold text-gold uppercase tracking-wider">
              30 anni di storia
            </span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black">
            <span className="text-gold">ALBO</span> D'ORO
          </h1>
          <p className="mt-3 text-slate-400 text-sm sm:text-base max-w-xl mx-auto">
            Le squadre che hanno conquistato il Torneo Notturno di Viadana Bresciana edizione dopo edizione.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : champions.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <Calendar className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-bold mb-2">Albo d'oro in arrivo</h3>
            <p className="text-slate-400">Le edizioni precedenti verranno pubblicate a breve.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {champions.map((c) => <ChampionCard key={c.id} champion={c} />)}
          </div>
        )}
      </div>
    </div>
  );
};

export default AlboDoroPage;
